import * as THREE from "three"
import { FEATURED_PROJECTS } from "./projects"

export type DestinationId = "earth" | "mars" | "moon" | "frontier-world"

export type Destination = {
  id: DestinationId
  label: string
  blurb: string
  position: THREE.Vector3
  color: number
}

/** Three signature bodies instead of abstract neon orbs. */
export const DESTINATIONS: Destination[] = [
  {
    id: "earth",
    label: "EARTH",
    blurb: "Blue homeworld. Engineering and living systems.",
    position: new THREE.Vector3(-9, 0.5, -95),
    color: 0x2f6fbf,
  },
  {
    id: "mars",
    label: "MARS",
    blurb: "Rust frontier. Ambition, risk, and AI exploration.",
    position: new THREE.Vector3(11, 2.2, -128),
    color: 0xc45a2a,
  },
  {
    id: "moon",
    label: "THE MOON",
    blurb: "Silver companion. Creative technology and sound.",
    position: new THREE.Vector3(-6, 3.5, -158),
    color: 0xb8c0cc,
  },
  {
    id: "frontier-world",
    label: "DEEP FRONTIER",
    blurb: "Beyond the local system. Games and new worlds.",
    position: new THREE.Vector3(10, -1.2, -188),
    color: 0x3ad0ff,
  },
]

export type PlanetSystem = {
  group: THREE.Group
  destinations: Destination[]
  setVisibility: (v: number) => void
  nearest: (cam: THREE.Vector3) => Destination | null
  update: (time: number) => void
  dispose: () => void
}

function paintBody(
  kind: "earth" | "mars" | "moon" | "ice",
  size = 512,
): THREE.CanvasTexture {
  const c = document.createElement("canvas")
  c.width = size
  c.height = size
  const g = c.getContext("2d")!

  if (kind === "earth") {
    const sky = g.createLinearGradient(0, 0, size, size)
    sky.addColorStop(0, "#0b2a5c")
    sky.addColorStop(0.45, "#1a5f9e")
    sky.addColorStop(1, "#0e3d6e")
    g.fillStyle = sky
    g.fillRect(0, 0, size, size)
    for (let i = 0; i < 28; i++) {
      g.fillStyle = `rgba(${40 + Math.random() * 80},${110 + Math.random() * 90},${40 + Math.random() * 50},${0.45 + Math.random() * 0.4})`
      g.beginPath()
      g.ellipse(
        Math.random() * size,
        Math.random() * size,
        18 + Math.random() * 70,
        10 + Math.random() * 36,
        Math.random() * Math.PI,
        0,
        Math.PI * 2,
      )
      g.fill()
    }
    g.fillStyle = "rgba(240,245,255,0.55)"
    for (let i = 0; i < 18; i++) {
      g.beginPath()
      g.ellipse(
        Math.random() * size,
        Math.random() * size * 0.4,
        30 + Math.random() * 80,
        8 + Math.random() * 18,
        Math.random(),
        0,
        Math.PI * 2,
      )
      g.fill()
    }
  } else if (kind === "mars") {
    const dust = g.createRadialGradient(size * 0.4, size * 0.35, 20, size * 0.5, size * 0.5, size * 0.7)
    dust.addColorStop(0, "#e08a4a")
    dust.addColorStop(0.5, "#b04820")
    dust.addColorStop(1, "#5a2010")
    g.fillStyle = dust
    g.fillRect(0, 0, size, size)
    for (let i = 0; i < 40; i++) {
      g.fillStyle = `rgba(${90 + Math.random() * 80},${30 + Math.random() * 40},${10 + Math.random() * 20},${0.25 + Math.random() * 0.35})`
      g.beginPath()
      g.arc(Math.random() * size, Math.random() * size, 4 + Math.random() * 28, 0, Math.PI * 2)
      g.fill()
    }
  } else if (kind === "moon") {
    g.fillStyle = "#9aa3ad"
    g.fillRect(0, 0, size, size)
    for (let i = 0; i < 90; i++) {
      const shade = 70 + Math.floor(Math.random() * 90)
      g.fillStyle = `rgba(${shade},${shade},${shade + 8},${0.35 + Math.random() * 0.45})`
      g.beginPath()
      g.arc(Math.random() * size, Math.random() * size, 3 + Math.random() * 22, 0, Math.PI * 2)
      g.fill()
    }
  } else {
    const ice = g.createRadialGradient(size * 0.5, size * 0.45, 10, size * 0.5, size * 0.5, size * 0.65)
    ice.addColorStop(0, "#dff6ff")
    ice.addColorStop(0.55, "#4ec8ff")
    ice.addColorStop(1, "#123a55")
    g.fillStyle = ice
    g.fillRect(0, 0, size, size)
  }

  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 8
  tex.needsUpdate = true
  return tex
}

function makePlanet(
  radius: number,
  map: THREE.Texture,
  segments: number,
  emissive = 0x000000,
): THREE.Mesh {
  const mat = new THREE.MeshStandardMaterial({
    map,
    metalness: 0.08,
    roughness: 0.72,
    emissive,
    emissiveIntensity: emissive ? 0.25 : 0,
  })
  return new THREE.Mesh(new THREE.SphereGeometry(radius, segments, segments), mat)
}

const BODY_KIND: Record<DestinationId, "earth" | "mars" | "moon" | "ice"> = {
  earth: "earth",
  mars: "mars",
  moon: "moon",
  "frontier-world": "ice",
}

const BODY_RADIUS: Record<DestinationId, number> = {
  earth: 8.4,
  mars: 6.2,
  moon: 3.4,
  "frontier-world": 7.0,
}

export function createPlanets(segments: number): PlanetSystem {
  const group = new THREE.Group()
  group.name = "planets"
  group.visible = false

  const bodies: THREE.Object3D[] = []
  const textures: THREE.Texture[] = []

  DESTINATIONS.forEach((d, i) => {
    const root = new THREE.Group()
    root.position.copy(d.position)
    const map = paintBody(BODY_KIND[d.id])
    textures.push(map)
    const radius = BODY_RADIUS[d.id]
    const planet = makePlanet(radius, map, segments, d.id === "frontier-world" ? d.color : 0x000000)
    root.add(planet)

    const atmosphere = new THREE.Mesh(
      new THREE.SphereGeometry(
        radius * (d.id === "moon" ? 1.04 : 1.09),
        Math.max(16, Math.floor(segments / 2)),
        Math.max(16, Math.floor(segments / 2)),
      ),
      new THREE.MeshBasicMaterial({
        color: d.color,
        transparent: true,
        opacity: d.id === "moon" ? 0.06 : 0.14,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.BackSide,
      }),
    )
    root.add(atmosphere)

    if (d.id === "earth" || d.id === "frontier-world") {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(radius * 1.55, 0.1, 10, 96),
        new THREE.MeshBasicMaterial({
          color: d.id === "earth" ? 0x7de3ff : 0xffffff,
          transparent: true,
          opacity: 0.28,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        }),
      )
      ring.rotation.x = Math.PI * (0.42 + i * 0.05)
      root.add(ring)
    }

    const beacon = new THREE.PointLight(d.color, 1.8, 70, 2)
    root.add(beacon)

    group.add(root)
    bodies.push(root)
  })

  const sun = new THREE.Mesh(
    new THREE.SphereGeometry(9, 48, 48),
    new THREE.MeshBasicMaterial({ color: 0xffc58a }),
  )
  sun.position.set(55, 28, -210)
  const sunLight = new THREE.PointLight(0xffb070, 2.4, 420, 2)
  sunLight.position.copy(sun.position)
  group.add(sun, sunLight)

  const showcases: THREE.Mesh[] = []
  const loader = new THREE.TextureLoader()
  for (const project of FEATURED_PROJECTS) {
    const dest = DESTINATIONS.find((d) => d.id === project.destinationId)
    if (!dest) continue
    const tex = loader.load(project.image)
    tex.colorSpace = THREE.SRGBColorSpace
    textures.push(tex)
    const panel = new THREE.Mesh(
      new THREE.PlaneGeometry(14, 7.4),
      new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        opacity: 0.95,
        side: THREE.DoubleSide,
      }),
    )
    panel.position.set(dest.position.x + 10, dest.position.y + 4, dest.position.z + 6)
    const frame = new THREE.Mesh(
      new THREE.PlaneGeometry(14.4, 7.8),
      new THREE.MeshBasicMaterial({
        color: dest.color,
        transparent: true,
        opacity: 0.22,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    )
    frame.position.copy(panel.position)
    frame.position.z -= 0.05
    group.add(frame, panel)
    showcases.push(panel, frame)
  }

  return {
    group,
    destinations: DESTINATIONS,
    setVisibility(v) {
      const visibility = THREE.MathUtils.clamp(v, 0, 1)
      group.visible = visibility > 0.02
      group.scale.setScalar(0.85 + visibility * 0.15)
    },
    nearest(cam) {
      let best: Destination | null = null
      let bestD = 48
      for (const d of DESTINATIONS) {
        const dist = cam.distanceTo(d.position)
        if (dist < bestD) {
          bestD = dist
          best = d
        }
      }
      return best
    },
    update(time) {
      bodies.forEach((b, i) => {
        b.rotation.y = time * (0.04 + i * 0.012)
        b.position.y = DESTINATIONS[i].position.y + Math.sin(time * 0.35 + i) * 0.3
      })
      for (const panel of showcases) {
        panel.lookAt(0, panel.position.y, panel.position.z + 40)
      }
    },
    dispose() {
      textures.forEach((t) => t.dispose())
      group.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose()
          const m = obj.material
          if (Array.isArray(m)) m.forEach((x) => x.dispose())
          else m.dispose()
        }
      })
    },
  }
}
